import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
} from '../../features/setting/setting.slice.ts'
import IdeyaTypography from '../../common/antd/ideya-typography.tsx'
import { CloseOutlined } from '@ant-design/icons'
import { isV2Ready } from '@/utils/helpers.ts'
import { IdeaListUserControls } from './components/idea-list-user-controls.tsx'
import {
  IdeaListTable,
  IdeaListTableRef,
} from './components/idea-list-table/idea-list-table.tsx'
import { CreateIdeaFormFieldTypes } from './components/idea-modal/idea-action/action-idea-form.tsx'
import { ActionIdeaModal } from './components/idea-modal/idea-action/action-idea-modal.tsx'
import { IdeaHistoryModal } from './components/idea-modal/idea-history/idea-history-modal.tsx'
import Alert from 'antd/es/alert/Alert'
import Row from 'antd/es/grid/row'
import Col from 'antd/es/grid/col'
import Form from 'antd/es/form/index'
import { SummaryInfo } from './components/idea-summary-info/idea-list-summary-info.tsx'
import {
  statisticsApi,
  useGetBuyingPowerQuery,
  useGetDeploymentDateRangeQuery,
  useGetFullyPnlQuery,
  useGetNetExposureQuery,
  useGetUserScoreQuery,
  useLazyGetBookSnapshotQuery,
} from '../../apis/statistics/statistics-api.api.ts'
import { selectIdeaListInput } from '../../redux/slices/idea-list-input.slice.ts'
import { selectUser } from '../../features/user/user.slice.ts'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import { useTranslation } from 'react-i18next'
import { IdeaActionLogModal } from './components/idea-modal/idea-action-log/idea-action-log-modal.tsx'
import { getCurrentQuarterRange } from '../../utils/time.ts'
import dayjs from 'dayjs'
import { useTableHeight } from '../../common/hooks/use-table-height.ts'
import {
  IdeaListMobile,
  IdeaListMobileRef,
} from './components/idea-list-mobile/idea-list-mobile.tsx'
import { IdeyaButton } from '../../common/antd/ideya-button/ideya-button.tsx'
import { NEW_RELIC, newRelic } from '../../utils/new-relic.ts'
import { IdeaListTour } from './components/idea-list-tour.tsx'
import { Theme } from '../../features/setting/setting.def.ts'
import tutorialBannerLight from '@assets/tutorial-banner-light.png'
import tutorialBannerDark from '@assets/tutorial-banner-dark.png'
import { SHOW_IDEA_LIST_TUTORIAL_BANNER } from '../../utils/constants.ts'
import {
  canCreateIdeaPermissions,
  hasPermissionAndFirmCheck,
} from '../../utils/permissions.ts'
import { useLazyClearBookIdeaAlertsQuery } from '../../apis/book/book-api.api.ts'
import { IdeaListUserFilterControlsMobile } from '@/pages/idea-list/components/idea-list-user-filter-controls-mobile.tsx'
import { IdeaListUserActionControls } from '@/pages/idea-list/components/idea-list-user-action-controls.tsx'

const MemoizedIdeaListTable = memo(IdeaListTable)
const MemoizedIdeaListMobile = memo(IdeaListMobile)

export const IdeaList = () => {
  const [form] = Form.useForm<CreateIdeaFormFieldTypes>()
  const [lastUpdatedTime, setLastUpdatedTime] = useState<Date>(new Date())
  const [showActionIdeaModal, setShowActionIdeaModal] = useState<boolean>(false)
  const [showIdeaHistoryModal, setShowIdeaHistoryModal] =
    useState<boolean>(false)
  const [showActionLogModal, setShowActionLogModal] = useState<boolean>(false)
  const [showLiquidateIdeaModal, setShowLiquidateIdeaModal] =
    useState<boolean>(false) 
  const [actionIdeaId, setActionIdeaId] = useState<string | null>(null)
  const [historyIdeaId, setHistoryIdeaId] = useState<string | null>(null)
  const themeStyle = useAppSelector(selectThemeStyle)
  const tableRef = useRef<IdeaListTableRef>(null)
  const mobileRef = useRef<IdeaListMobileRef>(null)
  const ideaListInput = useAppSelector(selectIdeaListInput)
  const userInfo = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const screens = useFixedBreakpoint()
  const isMobile = !screens.md
  const { t } = useTranslation()
  const headerRef = useRef<HTMLDivElement>(null)
  const tableHeight = useTableHeight(headerRef)
  const [openTour, setOpenTour] = useState<boolean>(false)
  const colorTheme = useAppSelector(selectTheme)
  const isDarkTheme = colorTheme === Theme.Dark
  const [showTutorialBanner, setShowTutorialBanner] = useState<boolean>(() => {
    const saved = localStorage.getItem(SHOW_IDEA_LIST_TUTORIAL_BANNER)
    return saved === null ? true : saved === 'true'
  })

  const bookId =
    ideaListInput.contributor === '' ? userInfo.bookId : ideaListInput.bookId

  const canCreateIdea = hasPermissionAndFirmCheck(
    canCreateIdeaPermissions,
    userInfo.firmId,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const shouldSkip =
    !bookId ||
    !ideaListInput.dateRange.length ||
    ideaListInput.dateRange.some((d) => d === null)

  const [ClearBookIdeaAlerts] = useLazyClearBookIdeaAlertsQuery()

  const {
    data: buyingPowerData,
    isLoading: isBuyingPowerLoading,
    isFetching: isBuyingPowerFetching,
  } = useGetBuyingPowerQuery(
    { bookId },
    {
      skip: shouldSkip,
    }
  )

  const {
    data: netExposureData,
    isLoading: isNetExposureLoading,
    isFetching: isNetExposureFetching,
  } = useGetNetExposureQuery(
    { bookId },
    {
      skip: shouldSkip,
    }
  )

  const {
    data: fullyPnlData,
    isLoading: isFullyPnlLoading,
    isFetching: isFullyPnlFetching,
  } = useGetFullyPnlQuery(
    {
      bookId,
    },
    { skip: shouldSkip }
  )

  const [
    getSnapshotData,
    {
      currentData: snapshotData,
      isLoading: isSnapshotLoading,
      isFetching: isSnapshotFetching,
    },
  ] = useLazyGetBookSnapshotQuery()

  const {
    data: deploymentData,
    isLoading: isDeploymentLoading,
    isFetching: isDeploymentFetching,
  } = useGetDeploymentDateRangeQuery(
    {
      bookId: bookId,
      startDate: dayjs(ideaListInput.dateRange[0]).format('YYYY-MM-DD'),
      endDate: dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD'),
    },
    { skip: shouldSkip }
  )

  const {
    data: userScoreData,
    isLoading: isUserScoreLoading,
    isFetching: isUserScoreFetching,
  } = useGetUserScoreQuery({ bookId }, { skip: shouldSkip })

  // Memoized callback to update last updated time
  const handleTableRefreshed = useCallback(() => {
    // Refresh the side metrics by invalidating their cache tag. Only queries
    // with an active subscription refetch; skipped/unstarted ones are a no-op,
    // so this never throws "Cannot refetch a query that has not been started
    // yet" (unlike calling refetch() directly during the bookId arg transition).
    dispatch(statisticsApi.util.invalidateTags(['statistics']))
    setLastUpdatedTime(new Date())
  }, [dispatch])

  const handleCloseActionIdeaModal = () => {
    form.resetFields()
    setActionIdeaId(null)
    setShowActionIdeaModal(false)
    tableRef.current?.refresh()
    mobileRef.current?.refresh()
  }

  useEffect(() => {
    const handler = () => {
      tableRef.current?.refresh()
      mobileRef.current?.refresh()
    }
    window.addEventListener('refresh-idea-list', handler)
    return () => window.removeEventListener('refresh-idea-list', handler)
  }, [])

  useEffect(() => {
    if (
      ideaListInput.dateRange !== getCurrentQuarterRange() &&
      dayjs(ideaListInput.dateRange[1]).isValid()
    ) {
      // Internal admins should not call getSnapshotData with empty bookId
      if (userInfo.role !== '') {
        if (ideaListInput.bookId) {
          getSnapshotData({
            bookId: ideaListInput.bookId,
            date: dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD') || '',
          })
        }
      } else {
        if (ideaListInput.contributor === '') {
          const bookIdQuery = userInfo.bookId || ideaListInput.bookId
          if (bookIdQuery) {
            getSnapshotData({
              bookId: bookIdQuery,
              date:
                dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD') || '',
            })
          }
        } else {
          if (ideaListInput.bookId) {
            getSnapshotData({
              bookId: ideaListInput.bookId,
              date:
                dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD') || '',
            })
          }
        }
      }
    }
  }, [ideaListInput.bookId, ideaListInput.dateRange, userInfo.bookId])

  const endDate = dayjs(ideaListInput.dateRange[1]).format('YYYY-MM-DD')
  const isQuarterEnd = endDate === getCurrentQuarterRange()[1]
  const isFutureOrToday =
    dayjs(endDate).isSame(dayjs(), 'day') ||
    dayjs(endDate).isAfter(dayjs(), 'day')
  const isValidEndDate = dayjs(ideaListInput.dateRange[1]).isValid()
  const isHistorical = !isQuarterEnd && !isFutureOrToday && isValidEndDate

  const handleEditIdea = useCallback((id: string) => {
    setActionIdeaId(id)
    setShowActionIdeaModal(true)
  }, [])

  const handleViewIdea = useCallback((id: string) => {
    setHistoryIdeaId(id)
    setShowActionLogModal(true)
  }, [])

  const handleViewHistoryIdea = useCallback((id: string) => {
    setHistoryIdeaId(id)
    setShowIdeaHistoryModal(true)
  }, [])

  const handleLiquidateIdea = useCallback((id: string) => {
    setActionIdeaId(id)
    setShowActionIdeaModal(true)
    setShowLiquidateIdeaModal(true)
  }, [])

  const handleCloseTutorialBanner = () => {
    setShowTutorialBanner(false)
    localStorage.setItem(SHOW_IDEA_LIST_TUTORIAL_BANNER, 'false')
  }

  const handleClearIdeaAlerts = () => {
    ClearBookIdeaAlerts({ bookId: userInfo.bookId }).then(() => {
      tableRef.current?.refresh()
      mobileRef.current?.refresh()
      setLastUpdatedTime(new Date())
    })
  }

  const handleReloadList = useMemo(
    () =>
      debounce(() => {
        tableRef.current?.refresh()
        mobileRef.current?.refresh()
        setLastUpdatedTime(new Date())
        dispatch(statisticsApi.util.invalidateTags(['statistics']))
      }, 500),
    [dispatch]
  )

  return (
    <>
      <div ref={headerRef} style={{ marginBottom: themeStyle.marginBase }}>
        <div style={{ marginBottom: themeStyle.marginBase }}>
          {showTutorialBanner && !isMobile && (
            <div
              style={{
                position: 'relative',
                borderRadius: '8px',
                border: `1px solid ${themeStyle.colorBorder}`,
                boxShadow:
                  '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
                backgroundImage: `url(${isDarkTheme ? tutorialBannerDark : tutorialBannerLight})`,
                backgroundSize: '100% 100%',
                width: '100%',
                backgroundPosition: 'top left',
                backgroundRepeat: 'no-repeat',
                overflow: 'hidden',
                marginBottom: themeStyle.marginMD,
              }}
            >
              <div
                style={{
                  padding: '22px 49px',
                }}
              >
                <CloseOutlined
                  onClick={handleCloseTutorialBanner}
                  style={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    cursor: 'pointer',
                    fontSize: 16,
                    padding: 4,
                  }}
                />
                <Row style={{ marginBottom: themeStyle.marginXXS }}>
                  <IdeyaTypography.Text
                    fontWeight={500}
                    style={{
                      color: isDarkTheme ? themeStyle.yellow10 : '#4251fe',
                    }}
                  >
                    {t('tutorial.idea_list.welcome_to_ideya')}
                  </IdeyaTypography.Text>
                </Row>
                <Row style={{ marginBottom: themeStyle.marginXS }}>
                  <IdeyaTypography.Text>
                    {t('tutorial.idea_list.banner_description')}
                  </IdeyaTypography.Text>
                </Row>
                <Row gutter={[8, 0]} align={'bottom'}>
                  <Col>
                    <IdeyaButton
                      size={'small'}
                      style={{
                        backgroundColor: '#1677ff',
                        color: 'white',
                      }}
                      onClick={() => setOpenTour(true)}
                    >
                      {t('tutorial.start_tutorial')}
                    </IdeyaButton>
                  </Col>
                  <Col>
                    <a
                      href={'https://asset.polymerideya.com/faq/en'}
                      target={'_blank'}
                      rel={'noopener noreferrer'}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                      }}
                    >
                      <IdeyaTypography.Text
                        style={{
                          color: '#1677ff',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {isDarkTheme
                          ? t('tutorial.idea_list.view_more_on_faq_page')
                          : t('tutorial.idea_list.view_faq_page')}
                      </IdeyaTypography.Text>
                    </a>
                  </Col>
                  <Col>
                    <div
                      onClick={() => window.open('/rules', '_blank')}
                      style={{
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <IdeyaTypography.Text
                        style={{
                          color: '#1677ff',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        {t('tutorial.idea_list.rules')}
                      </IdeyaTypography.Text>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          )}
          <SummaryInfo
            useSnapshotData={isHistorical}
            snapshotData={snapshotData?.data}
            buyingPower={buyingPowerData}
            netExposure={netExposureData?.data}
            pnlData={fullyPnlData?.data}
            isBuyingPowerLoading={
              isBuyingPowerLoading ||
              isBuyingPowerFetching ||
              isSnapshotLoading ||
              isDeploymentFetching ||
              isDeploymentLoading
            }
            isNetExposureLoading={
              isNetExposureLoading || isNetExposureFetching || isSnapshotLoading
            }
            isFullyPnlLoading={isFullyPnlLoading || isFullyPnlFetching}
            deployment={deploymentData?.data.deployment}
            starScore={userScoreData?.data.latestScore}
            previousScore={userScoreData?.data.previousScore}
            isStarScoreLoading={
              isUserScoreLoading ||
              isUserScoreFetching ||
              isSnapshotLoading ||
              isSnapshotFetching
            }
          />
        </div>
        {isV2Ready && (
          <Alert
            type={'success'}
            closable
            showIcon
            message={'Net Exposure Breach: 12%'}
          />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: themeStyle.marginBase,
          }}
        >
          <IdeaListUserControls />
          {isMobile && <IdeaListUserFilterControlsMobile />}
        </div>
      </div>
      <IdeaListUserActionControls
        onCreateIdea={() => setShowActionIdeaModal(true)}
        onClearIdeaAlerts={handleClearIdeaAlerts}
        onReloadList={handleReloadList}
        lastUpdatedTime={lastUpdatedTime}
        style={
          isMobile && ideaListInput.mobileView === 'card'
            ? { marginBottom: themeStyle.marginBase }
            : {
                borderRadius: '.5rem .5rem 0 0',
              }
        }
      />
      {isMobile && ideaListInput.mobileView === 'card' ? (
        <MemoizedIdeaListMobile
          ref={mobileRef}
          onViewIdea={handleViewIdea}
          onEditIdea={handleEditIdea}
          onRefreshed={handleTableRefreshed}
        />
      ) : (
        <MemoizedIdeaListTable
          ref={tableRef}
          onRefreshed={handleTableRefreshed}
          onEditIdea={handleEditIdea}
          onViewIdea={handleViewIdea}
          onViewHistoryIdea={handleViewHistoryIdea}
          onLiquidateIdea={handleLiquidateIdea}
          height={tableHeight}
        />
      )}
      {isMobile && canCreateIdea && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <IdeyaButton
            type={'primary'}
            block
            icon={<span style={{ fontWeight: 700, fontSize: 18 }}>+</span>}
            onClick={() => {
              setShowActionIdeaModal(true)
              newRelic.addPageAction(NEW_RELIC.PAGE_ACTION.clickCreateIdea)
            }}
            style={{
              borderRadius: '40px',
              boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
              fontWeight: 400,
              fontSize: themeStyle.fontSizeLG,
              border: `1px solid ${themeStyle.createIdeaBorderColor}`,
              background: themeStyle.createIdeaBgColor,
              color: themeStyle.createIdeaTextColor,
            }}
            className={'remove-compact'}
          >
            {t('common.create_idea')}
          </IdeyaButton>
        </div>
      )}
      <ActionIdeaModal
        showActionModal={showActionIdeaModal}
        showLiquidateModal={showLiquidateIdeaModal}
        setShowLiquidatedModal={setShowLiquidateIdeaModal}
        form={form}
        handleCloseModal={handleCloseActionIdeaModal}
        onIdeaActioned={() => {
          setTimeout(() => {
            tableRef.current?.refresh()
            mobileRef.current?.refresh()
            dispatch(statisticsApi.util.invalidateTags(['statistics']))
          }, 1000)
        }}
        editIdeaId={actionIdeaId}
        setEditIdeaId={setActionIdeaId}
      />
      <IdeaHistoryModal
        showHistoryModal={showIdeaHistoryModal}
        ideaId={historyIdeaId}
        handleCloseModal={() => {
          setHistoryIdeaId(null)
          setShowIdeaHistoryModal(false)
        }}
      />
      <IdeaActionLogModal
        showActionLogModal={showActionLogModal}
        ideaId={historyIdeaId}
        handleCloseModal={() => setShowActionLogModal(false)}
        handleRefreshTable={() => {
          tableRef.current?.refresh()
          mobileRef.current?.refresh()
        }}
      />
      <IdeaListTour openTour={openTour} setOpenTour={setOpenTour} />
    </>
  )
}
